import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import Layout from "../../components/Layout.tsx";
import Title from "../../components/Title.tsx";
import { Panel, PanelHeader } from "../../components/panel/panel.jsx";
import Loader from "../../components/Loader.tsx";
import { notification } from "../../utils/Notifications.tsx";
import { useAppSettings } from "../../hooks/useAppSettings.tsx";
import {
  getAllAuthorizedPermissions,
  createAuthorizedPermission,
  updateAuthorizedPermission,
  deleteAuthorizedPermission,
  AuthorizedPermission,
} from "../../services/authorized-permissions.service.ts";

const AuthorizedPermissions = () => {
  useAppSettings();
  const [loading, setLoading] = useState(false);
  const [authorizedPermissions, setAuthorizedPermissions] = useState<AuthorizedPermission[]>([]);
  const [editingId, setEditingId] = useState<number | null>(null);

  const formik = useFormik({
    initialValues: {
      title: "",
      maxDays: 1,
      requiresEvidence: false,
    },
    onSubmit: async (values) => {
      setLoading(true);
      try {
        if (editingId) {
          await updateAuthorizedPermission(editingId, values);
          notification("Éxito", "Permiso autorizado actualizado correctamente", "success");
        } else {
          await createAuthorizedPermission(values);
          notification("Éxito", "Permiso autorizado creado correctamente", "success");
        }
        await fetchAuthorizedPermissions();
        handleCancel();
      } catch (error: any) {
        notification(
          "Error",
          error.message || "Error al guardar el permiso autorizado",
          "danger"
        );
      } finally {
        setLoading(false);
      }
    },
  });

  const fetchAuthorizedPermissions = async () => {
    setLoading(true);
    try {
      const data = await getAllAuthorizedPermissions();
      setAuthorizedPermissions(data || []);
    } catch (error: any) {
      notification("Error", "Error al cargar los permisos autorizados", "danger");
      setAuthorizedPermissions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAuthorizedPermissions();
  }, []);

  const handleEdit = (permission: AuthorizedPermission) => {
    setEditingId(permission.id || null);
    formik.setValues({
      title: permission.title || "",
      maxDays: permission.maxDays || 1,
      requiresEvidence: permission.requiresEvidence || false,
    });
  };

  const handleCancel = () => {
    setEditingId(null);
    formik.resetForm();
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("¿Estás seguro de que deseas eliminar este permiso autorizado?")) {
      return;
    }

    setLoading(true);
    try {
      await deleteAuthorizedPermission(id);
      notification("Éxito", "Permiso autorizado eliminado correctamente", "success");
      await fetchAuthorizedPermissions();
    } catch (error: any) {
      notification("Error", "Error al eliminar el permiso autorizado", "danger");
    } finally {
      setLoading(false);
    }
  };


  return (
    <Layout>
      <Title
        baseTitle="Permisos"
        basePath="/permisos"
        title="Permisos Autorizados"
      />

      <div className="card border-0 m-4">
        <Panel>
          <PanelHeader noButton={true} className="bg-azul">
            {editingId ? "Editar Permiso Autorizado" : "Nuevo Permiso Autorizado"}
          </PanelHeader>
          <div className="p-4">
            <form onSubmit={formik.handleSubmit}>
              <div className="row">
                <div className="col-md-12 mb-3">
                  <label className="form-label">
                    Título <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="title"
                    value={formik.values.title}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Ej: Permiso médico, Permiso personal, etc."
                    required
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    Días Máximo de Solicitar <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    name="maxDays"
                    min="1"
                    value={formik.values.maxDays}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    required
                  />
                  <small className="form-text text-muted">
                    Número máximo de días que se pueden solicitar para este tipo de permiso
                  </small>
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">¿Requiere Evidencia?</label>
                  <div className="form-check mt-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="requiresEvidence"
                      checked={formik.values.requiresEvidence}
                      onChange={(e) =>
                        formik.setFieldValue("requiresEvidence", e.target.checked)
                      }
                    />
                    <label className="form-check-label" htmlFor="requiresEvidence">
                      Este permiso requiere evidencia/documentación
                    </label>
                  </div>
                </div>


                <div className="col-md-12 d-flex justify-content-end gap-2 mt-3">
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={handleCancel}
                    type="button"
                    disabled={loading}
                  >
                    Cancelar
                  </button>
                  <button
                    className="btn btn-azul btn-sm"
                    onClick={() => formik.handleSubmit()}
                    type="submit"
                    disabled={loading || !formik.values.title}
                  >
                    {editingId ? "Actualizar" : "Guardar"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </Panel>
      </div>

      <div className="card border-0 m-4">
        <Panel>
          <PanelHeader noButton={true} className="bg-azul">
            Listado de Permisos Autorizados
          </PanelHeader>
          <div className="p-4">
            <div className="table-responsive">
              <table className="table table-hover table-panel text-nowrap align-middle mb-0">
                <thead>
                  <tr>
                    <th className="text-center">ID</th>
                    <th className="text-center">Título</th>
                    <th className="text-center">Días Máximo</th>
                    <th className="text-center">Requiere Evidencia</th>
                    <th className="text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {authorizedPermissions.length > 0 ? (
                    authorizedPermissions.map((permission) => (
                      <tr key={permission.id}>
                        <td className="text-center">{permission.id}</td>
                        <td className="text-center">{permission.title}</td>
                        <td className="text-center">{permission.maxDays}</td>
                        <td className="text-center">
                          {permission.requiresEvidence ? (
                            <span className="badge bg-success">Sí</span>
                          ) : (
                            <span className="badge bg-secondary">No</span>
                          )}
                        </td>
                        <td className="text-center">
                          <div className="d-flex gap-2 justify-content-center">
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => handleEdit(permission)}
                              title="Editar"
                            >
                              <i className="fa fa-edit"></i>
                            </button>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => permission.id && handleDelete(permission.id)}
                              title="Eliminar"
                            >
                              <i className="fa fa-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-4">
                        <div className="py-4">
                          <h5 className="text-secondary text-center fs-20px">
                            Aún no existen permisos autorizados registrados
                          </h5>
                          <h5 className="text-center text-secondary fw-400 fs-15px">
                            Agrega un nuevo permiso autorizado usando el formulario de arriba
                          </h5>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </Panel>
      </div>

      <Loader isLoading={loading} />
    </Layout>
  );
};

export default AuthorizedPermissions;

